import { useAuth } from "@workos-inc/authkit-react";
import { useConvexAuth } from "convex/react";
import { type ReactNode, useEffect, useState } from "react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { router } from "@/lib/router";

export const SIGN_IN_KEY = "auth_sign_in_attempted";
const SIGN_IN_TIMEOUT = 30000;
export const PENDING_INVITE_TOKEN_KEY = "pending_invite_token";

/** Routes that render without forcing sign-in */
function isPublicRoute(): boolean {
	return globalThis.location.pathname.startsWith("/invite/");
}

export function AuthProvider({ children }: Readonly<{ children: ReactNode }>) {
	const { signIn } = useAuth();
	const { isLoading, isAuthenticated } = useConvexAuth();
	const [authError, setAuthError] = useState<string | null>(null);

	const isOAuthCallback = new URLSearchParams(globalThis.location.search).has(
		"code",
	);

	// On auth, clear sign-in state and redirect to pending invite
	useEffect(() => {
		if (!isAuthenticated) return;
		sessionStorage.removeItem(SIGN_IN_KEY);

		const pendingToken = sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY);
		if (pendingToken) {
			sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
			router.navigate({
				to: "/invite/$token",
				params: { token: pendingToken },
			});
		}
	}, [isAuthenticated]);

	// On callback, reset timer — email OAuth easily exceeds the 30s window
	useEffect(() => {
		if (isLoading || isAuthenticated || authError || isPublicRoute()) return;
		const signInTime = sessionStorage.getItem(SIGN_IN_KEY);

		if (isOAuthCallback) {
			if (!signInTime) return;
			sessionStorage.setItem(SIGN_IN_KEY, String(Date.now()));
			const id = setTimeout(
				() =>
					setAuthError(
						"Authentication timed out. Check that the correct CORS origin is configured in the WorkOS Dashboard and that your WorkOS client ID is correct.",
					),
				SIGN_IN_TIMEOUT,
			);
			return () => clearTimeout(id);
		}

		if (!signInTime) {
			sessionStorage.setItem(SIGN_IN_KEY, String(Date.now()));
			void signIn();
			return;
		}

		const remaining = SIGN_IN_TIMEOUT - (Date.now() - Number(signInTime));
		if (remaining <= 0) {
			setAuthError("Authentication failed. Please try again.");
			return;
		}
		const id = setTimeout(
			() => setAuthError("Authentication failed. Please try again."),
			remaining,
		);
		return () => clearTimeout(id);
	}, [isLoading, isAuthenticated, authError, isOAuthCallback, signIn]);

	if (isLoading) return <LoadingScreen isLoading />;
	if (isAuthenticated) return <>{children}</>;

	if (authError) {
		return (
			<div className="flex min-h-screen flex-col items-center justify-center gap-4 p-4">
				<h1 className="text-xl font-semibold">Authentication Error</h1>
				<p className="text-muted-foreground">{authError}</p>
				<button
					type="button"
					onClick={() => {
						sessionStorage.removeItem(SIGN_IN_KEY);
						setAuthError(null);
						router.navigate({ to: "/" });
					}}
					className="rounded bg-primary px-4 py-2 text-primary-foreground"
				>
					Try Again
				</button>
			</div>
		);
	}

	if (isOAuthCallback) return <LoadingScreen isLoading />;
	if (isPublicRoute()) return <>{children}</>;

	return <LoadingScreen isLoading />;
}
